import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import NeedHelpModel from "../NeedHelpModel";

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isNeedHelpOpen, setIsNeedHelpOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if both fields are empty
    if (!formData.email.trim() && !formData.password.trim()) {
      toast.error("Please fill in the data before signing in");
      return;
    }

    // Validate individual fields
    if (!formData.email.trim()) {
      toast.error("Email address is required");
      return;
    }
    if (!formData.password.trim()) {
      toast.error("Password is required");
      return;
    }

    setIsLoading(true);

    try {
      await login(formData.email, formData.password, rememberMe);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      let errorMessage = "Invalid login details";

      // Handle authentication errors with specific messages
      if (error.response?.status === 401) {
        const serverMessage = error.response?.data?.message || "";

        if (serverMessage.includes("You are not registered")) {
          errorMessage = "You are not registered";
        } else if (serverMessage.includes("Your password is invalid")) {
          errorMessage = "Your password is invalid";
        } else if (serverMessage.includes("Your account has been deactivated")) {
          errorMessage = "Your account has been deactivated";
        } else {
          errorMessage = "Invalid login details";
        }
      } else {
        errorMessage = error.response?.data?.message || error.message || "Invalid login details";
      }

      // Clear any existing toasts first
      toast.dismiss();

      // Show toast popup with a slight delay to ensure it's not cleared
      setTimeout(() => {
        toast.error(errorMessage, {
          duration: 10000, // Increased to 10 seconds
          position: "top-right",
          id: `login-error-${Date.now()}`,
          style: {
            background: "#363636",
            color: "#fff",
            zIndex: 9999,
          },
        });
      }, 200);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      <div className="before:contents-[''] relative flex min-h-screen items-center justify-center bg-navy-900 bg-opacity-90 py-20 before:absolute before:inset-0 before:z-[-1] before:bg-login-screen before:bg-cover before:bg-center before:bg-no-repeat">
        <div className="w-full max-w-md px-4">
          {/* Logo and Header */}
          <div className="fade-in mb-8 px-4 text-center">
            <h1 className="text-4xl font-bold mb-2">
              <span className="text-red-500 uppercase">Minesure Report</span>{" "}
              <span className="text-red-500 uppercase">Handling System</span>
            </h1>
          </div>

          {/* Login Form */}
          <div>
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {/* Email Field */}
              <div className="relative h-16 w-full">
                <label
                  htmlFor="email"
                  className="absolute left-4 top-2.5 z-10 m-0 block text-xxs font-normal uppercase leading-3 text-navy-900"
                >
                  Email Address:
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="text-color-gray-0 absolute inset-0 h-auto rounded-xl border-0 bg-gray-0 px-4 pt-6 hover:outline-none focus:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  placeholder=""
                />
              </div>

              {/* Password Field */}
              <div className="relative h-16 w-full">
                <label
                  htmlFor="password"
                  className="text-color-gray-0 absolute left-4 top-2.5 z-10 m-0 block text-xxs font-normal uppercase leading-3"
                >
                  Password:
                </label>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="text-color-gray-0 absolute inset-0 h-auto rounded-xl border-0 bg-gray-0 px-4 pt-6 hover:outline-none focus:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  placeholder=""
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transform text-navy-900 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {/* Remember Me */}
              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded-3xl"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className="ml-4 text-sm font-normal uppercase text-gray-0">Remember me</span>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="h-16 w-full rounded-xl bg-red-500 text-sm font-normal uppercase text-gray-0 shadow-lg hover:shadow-xl"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">Sign In</div>
                )}
              </Button>
            </form>

            {/* Forgot Password Link */}
            <div className="mt-4 text-center">
              <Link
                to="/forgot-password"
                className="text-sm font-normal uppercase text-gray-0 transition-colors hover:text-red-600"
              >
                Forgot your password?
              </Link>
            </div>

            {/* Need Help Link */}
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsNeedHelpOpen(true)}
                className="text-sm font-normal uppercase text-gray-0 transition-colors hover:text-red-600"
              >
                Need Help?
              </button>
            </div>
          </div>
        </div>
      </div>
      <NeedHelpModel
        isOpen={isNeedHelpOpen}
        onClose={() => setIsNeedHelpOpen(false)}
      />
    </>
  );
};

export default LoginPage;
